import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Music, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/config";
import { MediaPreview } from "@/components/MediaPreview";
import axios from "axios";

const COLLECTIONS = {
  rehearsals: { title: "Choir Rehearsals", icon: Music, color: "#E8922F", desc: "Behind the scenes of our choir rehearsals — where the magic happens." },
  community: { title: "Community Events", icon: Users, color: "#C4392D", desc: "Moments from our community outreach, fellowship, and service events." },
};

export default function CollectionPage() {
  const { type } = useParams();
  const config = COLLECTIONS[type];
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!config) return;
    axios.get(`${API}/collections/${type}`)
      .then(({ data }) => setItems(data))
      .catch(err => console.error("Failed to load collection:", err.message));
  }, [type, config]); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  if (!config) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4A4A4A] text-lg mb-4">Collection not found.</p>
          <Link to="/"><Button className="btn-primary">Go Home</Button></Link>
        </div>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div data-testid={`collection-page-${type}`}>
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-16 md:py-24">
        <div className="section-container text-center">
          <Icon size={36} className="mx-auto mb-3" style={{ color: config.color }} />
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {config.title}
          </h1>
          <p className="text-gray-400 text-sm sm:text-base md:text-lg mt-4 max-w-xl mx-auto">
            {config.desc}
          </p>
        </div>
      </section>

      {/* Media Grid */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <Icon size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-[#4A4A4A] text-lg">No media uploaded yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {items.map((item) => {
                const isExternal = item.media_url?.startsWith("http");
                return (
                  <div
                    key={item.id}
                    className="rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => !isExternal && setSelected(item)}
                    data-testid={`collection-item-${item.id}`}
                  >
                    <div className="relative h-[220px] sm:h-[260px] bg-gray-100">
                      <MediaPreview item={item} hoverScale />
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider" style={{ backgroundColor: item.media_type === "video" ? "#C4392D" : "#E8922F", color: "white" }}>
                        {item.media_type === "video" ? "Video" : "Photo"}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-[#1A1A1A] text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.title}</h3>
                      {item.description && <p className="text-gray-400 text-xs mt-1 line-clamp-2">{item.description}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
          {selected && (
            <div className="relative">
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors">
                <X size={20} />
              </button>
              {selected.media_type === "video" ? (
                <video src={selected.media_url} className="w-full max-h-[80vh]" controls autoPlay />
              ) : (
                <img src={selected.media_url} alt={selected.title} className="w-full max-h-[80vh] object-contain" />
              )}
              <div className="p-6 bg-black/80">
                <h3 className="text-white font-bold text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selected.title}</h3>
                {selected.description && <p className="text-white/70 text-sm mt-1">{selected.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
